export type ChapterStat = {
    value: number
    decimals?: number
    prefix?: string
    suffix?: string
    label: string
    /** Show the value as is instead of counting up to it. */
    fixed?: boolean
}

export type ChapterFeature = {
    icon: 'siren' | 'radar' | 'gauge' | 'scale'
    text: string
}

export type StoryChapter = {
    id: string
    index: number
    number: string
    eyebrow: string
    title: string
    body: string
    side: 'left' | 'right'
    stats?: ChapterStat[]
    features?: ChapterFeature[]
}

export const hero = {
    eyebrow: "FQ-Series lithium electric forklifts",
    title: "Heavy lifting, electrified.",
    body: "FORQ forklifts lift up to 5,000 kg, run up to 16 hours on a single charge and cut running costs by up to 70% compared with diesel. Quiet enough for indoors, tough enough for the yard."
}

export const chapters: StoryChapter[] = [
    {
        id: 'power',
        index: 1,
        number: '01',
        eyebrow: "Power",
        title: "Two shifts. One charge.",
        body: "A lithium iron phosphate pack sits low in the chassis and feeds twin AC drive motors with instant torque. Top up during breaks and keep moving all day, with no battery swaps, no watering and no fumes.",
        side: 'left',
        stats: [
            { value: 4000, suffix: '+', label: "Full charge cycles" },
            { value: 1, suffix: ' h', label: "Fast charge to 80%" },
            { value: 16, suffix: ' h', label: "Runtime per charge" },
            { value: 0, label: "Tailpipe emissions" }
        ]
    },
    {
        id: 'lift',
        index: 2,
        number: '02',
        eyebrow: "Lift",
        title: "Lift 7.5 meters without a wobble.",
        body: "Nested high-tensile steel channels keep loads steady at full height. Proportional hydraulics place pallets to the millimeter, and regenerative lowering turns every descent back into charge.",
        side: 'right',
        stats: [
            { value: 5000, suffix: ' kg', label: "Rated capacity" },
            { value: 7.5, decimals: 1, suffix: ' m', label: "Max lift height" },
            { value: 0.55, decimals: 2, suffix: ' m/s', label: "Loaded lift speed" },
            { value: 12, suffix: '%', label: "Energy recovered when lowering" }
        ]
    },
    {
        id: 'safety',
        index: 3,
        number: '03',
        eyebrow: "Safety",
        title: "Safety you can see coming.",
        body: "A blue spot on the floor warns pedestrians before the truck arrives, red zone lines mark the no-go area, and 360° radar slows the truck automatically when someone steps too close.",
        side: 'left',
        features: [
            { icon: 'siren', text: "Blue spot and red zone lights" },
            { icon: 'radar', text: "360° pedestrian radar" },
            { icon: 'gauge', text: "Automatic speed reduction in turns" },
            { icon: 'scale', text: "Load weight and tilt sensing" }
        ]
    },
    {
        id: 'engineering',
        index: 4,
        number: '04',
        eyebrow: "Engineering",
        title: "Fewer parts. Nothing to fail.",
        body: "Sealed IP65 drive units, a 12 mm structural steel chassis and modular assemblies that swap out in under an hour. Less maintenance, less downtime and more years in service.",
        side: 'right',
        stats: [
            { value: 65, prefix: "IP", label: "Sealed drivetrain", fixed: true },
            { value: 60, suffix: '%', label: "Lower maintenance cost than diesel" },
            { value: 5, suffix: ' yr', label: "Battery and drivetrain warranty" },
            { value: 1, prefix: '< ', suffix: ' h', label: "To swap a major module", fixed: true }
        ]
    }
]

export const configureIntro = {
    number: '05',
    eyebrow: "Configure",
    title: "Build your FORQ",
    body: "Pick a model, a color and the options you need. The truck updates as you go."
}

export const marquee = [
    "5,000 kg capacity",
    "7.5 m lift height",
    "16 h runtime",
    "1 h fast charge",
    "IP65 sealed drivetrain",
    "Zero tailpipe emissions",
    "5 year warranty",
    "360° pedestrian radar"
]

export const industries: {
    id: string
    title: string
    body: string
    stat: string
    statLabel: string
    icon: 'warehouse' | 'snowflake' | 'factory' | 'trees'
}[] = [
    {
        id: 'warehousing',
        title: "Warehousing and logistics",
        body: "Narrow aisles, high racking and double shifts. Tight turning circles and 7.5 m of lift keep pallets flowing from dock to rack.",
        stat: "16 h",
        statLabel: "runtime for double shifts",
        icon: 'warehouse'
    },
    {
        id: 'cold-storage',
        title: "Cold storage",
        body: "Sealed electronics and heated controls keep working in the freezer, with no exhaust fumes in enclosed rooms.",
        stat: "−30 °C",
        statLabel: "rated operating temperature",
        icon: 'snowflake'
    },
    {
        id: 'manufacturing',
        title: "Manufacturing",
        body: "Quiet, emission-free handling right beside your production lines, with precise inching for tooling, dies and parts.",
        stat: "62 dB",
        statLabel: "at the operator's ear",
        icon: 'factory'
    },
    {
        id: 'building-supplies',
        title: "Building supplies and yards",
        body: "Pneumatic tires, an IP65 drivetrain and the all-weather cab handle timber, block and steel outdoors, rain or shine.",
        stat: "IP65",
        statLabel: "sealed against dust and rain",
        icon: 'trees'
    }
]

export const savingsAssumptions = {
    dieselCostPerHour: 3.4,
    dieselMaintenancePerHour: 2.1,
    electricCostPerHour: 0.7,
    electricMaintenancePerHour: 0.85,
    co2KgPerHour: 8.65
}

export const testimonials = [
    {
        quote: "We replaced eleven diesel trucks with FQ-30s and our energy bill for material handling dropped by more than half. The drivers noticed the quiet first.",
        name: "Dana Whitfield",
        role: "Operations Director",
        company: "Northgate Distribution",
        metric: "54%",
        metricLabel: "lower energy cost"
    },
    {
        quote: "Running two shifts in the freezer used to mean battery swaps at 3 a.m. Now we top up on breaks and the trucks never leave the floor.",
        name: "Marcus Oyelaran",
        role: "Site Manager",
        company: "Polar Fresh Logistics",
        metric: "0",
        metricLabel: "battery swaps per week"
    },
    {
        quote: "The FQ-50 handles full timber bundles in the yard, rain or shine. Service has been on site within a day every time we've called.",
        name: "Elena Ruiz",
        role: "Yard Supervisor",
        company: "Ruiz Timber and Supply",
        metric: "< 24 h",
        metricLabel: "service response"
    }
]

export const supportItems = [
    {
        icon: 'wallet',
        title: "Buy, lease or rent",
        body: "Own it outright, spread the cost over 36 to 60 months, or rent by the week for peak season."
    },
    {
        icon: 'wrench',
        title: "Service within 24 hours",
        body: "Factory-trained technicians with stocked vans in every region. Most fixes happen on the first visit."
    },
    {
        icon: 'smartphone',
        title: "FORQ Fleet app",
        body: "Live battery health, impact alerts and utilization reports for every truck, on any device."
    },
    {
        icon: 'graduation',
        title: "Operator training included",
        body: "Certified training for your drivers at handover, with refresher sessions whenever you need them."
    }
] as const

export const faqs = [
    {
        question: "How long does delivery take?",
        answer: "Standard configurations ship in 2 to 3 weeks. Builds with options such as the enclosed cab or fork positioner take 6 to 8 weeks. We confirm the date with your quote."
    },
    {
        question: "What do I need to charge a FORQ?",
        answer: "The FQ-18 comes with an onboard charger that runs from a single-phase 208 to 240 V outlet. The FQ-30 and FQ-50 use a wall-mounted fast charger on a three-phase circuit, and our team will help you plan and install it."
    },
    {
        question: "Do lithium batteries need maintenance?",
        answer: "No watering, no equalizing and no battery room. The pack manages itself and reports its health to the FORQ Fleet app, so you see problems long before they stop a shift."
    },
    {
        question: "Can I use a FORQ outdoors?",
        answer: "Yes. The drivetrain is sealed to IP65 and the pneumatic tire option grips on gravel, ramps and wet yards. Add the enclosed cab for year-round comfort."
    },
    {
        question: "What does the warranty cover?",
        answer: "The battery and drivetrain are covered for 5 years or 10,000 hours, and the rest of the truck for 2 years, whichever comes first."
    },
    {
        question: "Do you take trade-ins?",
        answer: "We accept trade-ins of any brand, whether diesel, LPG or electric, and credit their value against your new FORQ."
    },
    {
        question: "Can I lease instead of buying?",
        answer: "Yes. Leases run from 36 to 60 months with service included, and short-term rentals start at one week. Ask for both options in your quote."
    }
]

export const quotePerks = [
    "Tailored quote within one business day",
    "Free site survey and charging plan",
    "Trade-in valuation for your current fleet"
]

export const timelines = [
    "Within 1 month",
    "1 to 3 months",
    "3 to 6 months",
    "Just researching"
]

export const purchaseTypes = [
    "Buy",
    "Lease",
    "Rent",
    "Not sure yet"
]
